"use client";

import { getGizmoSession } from "@/lib/gizmoSession";
import { useState, useEffect, useCallback, useRef, Suspense } from "react";
import {hairRead, hairWrite, hairRemove} from "@/lib/hairStudio/storage";
import { parseHairShortlist } from "@/lib/hairFinderHandoff";
import dynamic from "next/dynamic";
import {
  Box,
  Container,
  Text,
  Button,
  SimpleGrid,
  Modal,
  Flex,
  Group,
  Skeleton,
  Badge,
  rem,
} from "@mantine/core";
import { Dropzone, type FileRejection } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import { IconCloudUpload, IconX, IconDownload, IconShare, IconLock, IconWand, IconSparkles, IconCheck } from "@tabler/icons-react";
import { useSearchParams, usePathname } from "next/navigation";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import firebase_app from "@/firebase/config";
import { HAIR_COLOR_FAMILIES, HAIR_COLORS, FREE_GENERATION_LIMIT, HairColorFamily } from "@/constants/hairColors";
import { PRICING } from "@/constants/pricing";
import { trackGizmo, trackCtaClick } from "@/lib/analytics";
import EmailResultsCapture from "@/components/EmailResultsCapture";
import CompareSlider from "./CompareSlider";
import HairColorDemo from "@/components/HairColorDemo";
import { HAIR_FAQS } from "./faqs";
import classes from "./hairColorChanger.module.css";

const HairStudio = dynamic(() => import("@/components/HairStudio"), {loading: () => <p role="status">Opening your Hair Color Studio…</p>});

const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);

const functions = getFunctions(firebase_app);
if (process.env.NODE_ENV !== "production") {
  connectFunctionsEmulator(functions, "localhost", 5001);
}
// Real generations can take up to ~2-3 minutes (green-base warm-up + per-color
// render); the Firebase callable default timeout is 70s, which is what was
// causing "nothing happened" — the client gave up long before the backend did.
const CALLABLE_TIMEOUT_MS = 300000;
const hairColorChangerFunction = httpsCallable(functions, "hairColorChangerFunction", {
  timeout: CALLABLE_TIMEOUT_MS,
});

type CallableResponse = {
  sourceId: string;
  results: { colorId: string; imageUrl: string }[];
  freeRemaining: number;
};

type TeaserResponse = {
  sourceId: string;
  teaserTiles: { colorId: string; imageUrl: string }[];
};

type PrepareResponse = {
  sourceId: string;
  prepared: true;
  freeRemaining: number;
};

const STYLING_TIPS = [
  "Tip: dimensional shades like balayage look most realistic on photos with visible hair texture.",
  "Tip: front-facing photos with even lighting give the most accurate color preview.",
  "Tip: you can compare Before/After once your result is ready — just flip the toggle.",
  "Tip: save two or three color directions to discuss with your stylist.",
];

// Copy blocks for the per-family SEO sections below the tool. Each family in
// HAIR_COLOR_FAMILIES gets a short, genuinely useful blurb.
const FAMILY_COPY: Record<HairColorFamily, string> = {
  blonde: "Compare a cool ash or platinum direction with a warmer honey or beige. Look at the change in lightness as well as the tone; the preview does not predict how your current hair would need to be lightened.",
  brunette: "Compare neutral, chestnut and ash browns at similar depths first. Then try a lighter or deeper shade to decide how much contrast you enjoy beside your face.",
  black: "Try Jet Black beside Soft Black or Off Black. Small changes in depth and reflect can make a strong color feel different, especially beside your brows and eyes.",
  red: "Compare bright copper, deeper cherry and muted red-brown directions. Save the shades you enjoy and discuss the color process and upkeep with your stylist.",
  auburn: "Auburn combines brown depth with red warmth. Compare light, classic and deep auburn to separate the amount of red from the amount of contrast you want.",
  balayage: "These dimensional previews explore a transition from roots to ends. They need a separate render, and timing varies. Use them to discuss placement and contrast with your colorist, not as a prediction of an exact salon result.",
  silver: "Compare natural gray, soft silver and deeper charcoal directions. Keeping your own gray is also an option; a season label does not require you to cover it.",
  pastel: "Compare pink, lilac, peach and other soft color directions. A digital pastel preview does not assess whether your current base can achieve the shade or how much upkeep it will need.",
  vivid: "Try a saturated blue, pink or purple when you want a deliberate statement. Compare the same photo across shades and choose the effect you enjoy; you do not need permission from an undertone category.",
  rose: "Explore dusty rose, rose gold and deeper rose-brown. Compare the strength of the pink and the lightness of the base separately when building your shortlist.",
};

const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Unreadable photo"));
    };
    img.onload = () => {
      try {
      let { width, height } = img;
      const scale = Math.min(maxWidth / width, maxHeight / height, 1);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("Photo canvas unavailable");
      ctx.drawImage(img, 0, 0, width, height);
      const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
      URL.revokeObjectURL(img.src);
      resolve(dataUrl);
      } catch (error) {
        URL.revokeObjectURL(img.src);
        reject(error);
      }
    };
    img.src = URL.createObjectURL(file);
  });
};

// Maps known callable error codes to friendly copy.
function mapErrorMessage(code: string | undefined): { title: string; message: string } {
  switch (code) {
    case "FREE_LIMIT_REACHED":
      return { title: "Free limit reached", message: "You've used all your free try-ons." };
    case "PAYMENT_REQUIRED":
      return { title: "Payment required", message: "Payment required to continue — please complete checkout." };
    case "PAYMENT_INVALID":
      return { title: "Payment issue", message: "We couldn't verify your payment. Please try checking out again." };
    case "BOARD_LIMIT_REACHED":
      return { title: "Limit reached", message: "You've hit the generation limit for this session." };
    case "INVALID_IMAGE":
      return { title: "Photo issue", message: "We couldn't read that photo clearly — please upload a clear, front-facing selfie." };
    default:
      return { title: "Something went wrong", message: "We couldn't generate your result. Please try again." };
  }
}

function getErrorCode(error: any): string | undefined {
  const msg = error?.message || error?.details || "";
  const known = ["FREE_LIMIT_REACHED", "PAYMENT_REQUIRED", "PAYMENT_INVALID", "BOARD_LIMIT_REACHED", "INVALID_IMAGE", "PREPARE_LIMIT_REACHED"];
  return known.find((code) => msg.includes(code));
}


// Progress state machine for the post-click generation UI. "mapping" shows
// while we're still waiting on the prepare-ahead green base (first try-on
// only, usually already resolved by the time the user picks a shade).
// "painting" shows while the actual color render is in flight.
type ProgressStage = "idle" | "mapping" | "painting";

function savePreviewBlob(blob: Blob, colorId: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const extension = blob.type.includes("png") ? "png" : blob.type.includes("webp") ? "webp" : "jpg";
  link.href = url;
  link.download = `hair-${colorId}.${extension}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// Keep URL-dependent client state below its own boundary so the full tool,
// guide and FAQs are present in the initial HTML for people and crawlers.
function QueryState({onChange}: {onChange: (params: URLSearchParams) => void}) {
  const searchParams = useSearchParams();
  useEffect(() => { onChange(new URLSearchParams(searchParams.toString())); }, [searchParams, onChange]);
  return null;
}

export type StudioAccess = {deviceId:string;sourceId:string;stripeSessionId:string};
type ContentProps = {embedded?:boolean;initialPhoto?:string;onStudioAccess?:(access:StudioAccess)=>void;onUsePreview?:(src:string,label:string)=>void};
function ContentInner({embedded=false,initialPhoto,onStudioAccess,onUsePreview}: ContentProps) {
  const newInitialPhoto = useRef(!!initialPhoto && initialPhoto !== hairRead("hairChangerImage", "session"));
  const accessNotified = useRef("");
  const [searchParams, setSearchParams] = useState(() => new URLSearchParams());
  const pathname = usePathname();
  const locale = pathname?.split("/")[1]?.length === 2 ? pathname.split("/")[1] : "en";

  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);
  const [openingPhoto, setOpeningPhoto] = useState(false);
  const [photoError, setPhotoError] = useState("");
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [selectedFamily, setSelectedFamily] = useState<string>("all");
  const [selectedColorId, setSelectedColorId] = useState<string | null>(null);
  // Select a valid catalog shade; this never uploads or generates an image.
  const requestedShade = searchParams.get("shade");
  const shortlistQuery = searchParams.get("shortlist");
  const [savedShortlist, setSavedShortlist] = useState("");
  const recommendedColors = parseHairShortlist(shortlistQuery || savedShortlist);
  useEffect(() => {
    try {
      if (shortlistQuery !== null) {
        const value = parseHairShortlist(shortlistQuery).map(color => color.id).join(",");
        hairWrite("hair_finder_shortlist", value, "session");
        setSavedShortlist(value);
      } else {
        setSavedShortlist(hairRead("hair_finder_shortlist", "session") || "");
      }
    } catch { /* Recommendations still work from the URL without storage. */ }
  }, [shortlistQuery]);
  useEffect(() => {
    const shade = HAIR_COLORS.find(color => color.id === requestedShade);
    if (shade) {
      setSelectedColorId(shade.id);
      setSelectedFamily(shade.family);
    }
  }, [requestedShade]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressStage, setProgressStage] = useState<ProgressStage>("idle");
  const [tipIdx, setTipIdx] = useState(0);
  const [freeResult, setFreeResult] = useState<{ colorId: string; imageUrl: string } | null>(null);
  const [freeResults, setFreeResults] = useState<{ colorId: string; colorName: string; hex: string; image: string }[]>([]);
  const [viewedResultIndex, setViewedResultIndex] = useState<number | null>(null);
  const [freeRemaining, setFreeRemaining] = useState<number>(FREE_GENERATION_LIMIT);
  const [freeLimitHit, setFreeLimitHit] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState<string | null>(null);
  const checkoutPreparing = useRef(false);
  const [paidSessionId, setPaidSessionId] = useState<string | null>(null);
  const [studioMode, setStudioMode] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [pendingPayment, setPendingPayment] = useState<string | null>(null);
  const verificationInFlight = useRef(false);

  // Upsell modal (teaser mosaic)
  const [teaserTiles, setTeaserTiles] = useState<{ colorId: string; imageUrl: string }[] | null>(null);
  const [teaserFailed, setTeaserFailed] = useState(false);
  const [upsellModalOpen, setUpsellModalOpen] = useState(false);
  const [upsellTrigger, setUpsellTrigger] = useState<"auto" | "cta" | "limit">("auto");
  const teaserRequestedRef = useRef(false);


  // Prepare-ahead: fired immediately after upload, kept as a promise so
  // "See it on me" can await it instead of re-uploading the photo.
  const preparePromiseRef = useRef<Promise<string | null> | null>(null);
  const photoGenerationRef = useRef(0);
  const [prepareResolved, setPrepareResolved] = useState(false);
  const familySectionRef = useRef<HTMLDivElement | null>(null);
  const uploadSectionRef = useRef<HTMLDivElement | null>(null);
  const openUploadRef = useRef<() => void>(null);

  // Device identity
  useEffect(() => {
    let id = hairRead("hair_device_id");
    if (!id) {
      id = crypto.randomUUID();
      hairWrite("hair_device_id", id);
    }
    setDeviceId(id);
  }, []);

  // A page view is not a CTA interaction.
  useEffect(() => {
    trackGizmo("tool_view", { tool: "hair", source: embedded ? "embedded" : "standalone" });
  }, []);

  // Restore persisted photo/source
  useEffect(() => {
    const storedRemaining = hairRead("hair_free_used");
    if (storedRemaining !== null && /^[0-3]$/.test(storedRemaining)) {
      setFreeRemaining(Number(storedRemaining));
      setFreeLimitHit(Number(storedRemaining) === 0);
    }
    const storedPhoto = hairRead("hairChangerImage", "session");
    if (initialPhoto || storedPhoto) setPhotoDataUrl(initialPhoto || storedPhoto);
    if (initialPhoto) hairWrite("hairChangerImage",initialPhoto,"session");
    if (newInitialPhoto.current) return;
    const storedSource = hairRead("hair_source_id", "session") || hairRead("hair_source_id");
    if (storedSource) {
      setSourceId(storedSource);
      setPrepareResolved(true);
    }
    const storedPaidSession = hairRead("hair_paid_session");
    if (storedPaidSession) {
      try {
        const context = JSON.parse(hairRead("hair_paid_context") || "null");
        if (context?.sessionId === storedPaidSession && context.sourceId && context.deviceId) {
          setSourceId(context.sourceId); setDeviceId(context.deviceId);
        }
      } catch { /* Older purchases use the existing source keys. */ }
      setPaidSessionId(storedPaidSession);
      setStudioMode(true);
    }
  }, []);

  useEffect(()=>{
    if (onStudioAccess && studioMode && deviceId && sourceId && paidSessionId && accessNotified.current!==paidSessionId) {
      accessNotified.current=paidSessionId;
      onStudioAccess({deviceId,sourceId,stripeSessionId:paidSessionId});
    }
  },[onStudioAccess,studioMode,deviceId,sourceId,paidSessionId]);

  // Rotating styling tips while generating
  useEffect(() => {
    if (!isGenerating) return;
    const interval = setInterval(() => {
      setTipIdx((i) => (i + 1) % STYLING_TIPS.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [isGenerating]);

  // Verify a Stripe session and unlock Studio mode
  const verifyPaidSession = useCallback(async (sessionId: string) => {
    if (verificationInFlight.current) return;
    verificationInFlight.current = true;
    setVerifyingPayment(true); setPendingPayment(sessionId); setCheckoutOpen(false);
    setPaymentMessage("Checking your payment and restoring your photo…");
    try {
      const res = await fetch("/api/hair-studio/restore", {method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({stripeSessionId:sessionId, sourceId:hairRead("hair_source_id","session") || hairRead("hair_source_id")})});
      const data = await res.json();
      if (res.ok && data.paid === true) {
        hairWrite("hair_paid_session", sessionId);
        hairWrite("hair_paid_context",JSON.stringify({sessionId,deviceId:data.deviceId,sourceId:data.sourceId}));
        hairWrite("hair_source_id",data.sourceId); hairWrite("hair_source_id",data.sourceId,"session");
        hairWrite("hair_device_id",data.deviceId);
        hairRemove("hair_pending_checkout");
        setDeviceId(data.deviceId); setSourceId(data.sourceId);
        setPaidSessionId(sessionId);
        setStudioMode(true);
        setCheckoutOpen(false);
        setPaymentMessage(""); setPendingPayment(null);

        const trackedFlag = `hair_purchase_tracked_${sessionId}`;
        if (!hairRead(trackedFlag)) {
          trackGizmo("hair_purchase");
          hairWrite(trackedFlag, "1");
        }
      } else {
        setPaymentMessage(data.error === "PAYMENT_PENDING" ? "Payment has not been confirmed yet. If you completed checkout, check again in a moment. You do not need to pay again." : "We couldn't restore your studio yet. Retry below or contact support with your receipt. You do not need to purchase again.");
      }
    } catch (err) {
      setPaymentMessage("The payment check could not connect. Your checkout has been saved; retry below without paying again.");
    } finally {verificationInFlight.current = false;setVerifyingPayment(false);}
  }, []);

  useEffect(() => {
    if(newInitialPhoto.current)return;
    const pending = hairRead("hair_pending_checkout");
    if (new URLSearchParams(window.location.hash.slice(1)).has("studio") || new URLSearchParams(window.location.search).has("session_id")) return;
    if (pending && !hairRead("hair_paid_session")) verifyPaidSession(pending);
  }, [verifyPaidSession]);

  useEffect(()=>{
    const restore = () => {
      const session = new URLSearchParams(window.location.hash.slice(1)).get("studio");
      if (session && /^cs_[A-Za-z0-9_]+$/.test(session)) {
        window.history.replaceState(window.history.state,"",window.location.pathname+window.location.search);
        verifyPaidSession(session);
      }
    };
    restore();window.addEventListener("hashchange",restore);
    return ()=>window.removeEventListener("hashchange",restore);
  },[verifyPaidSession]);

  // Verify Stripe session on return (fallback path, e.g. restored tab)
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    verifyPaidSession(sessionId);
  }, [searchParams, verifyPaidSession]);

  // Fires the prepare-ahead call immediately after upload: stores the source
  // photo and warms its green hair base so the first real generation is fast.
  // Never blocks the UI and never surfaces an error — PREPARE_LIMIT_REACHED
  // (and any other failure) just falls back to lazy generation on submit.
  const startPrepare = useCallback(
    (dataUrl: string, id: string) => {
      const generation = ++photoGenerationRef.current;
      const startedAt = Date.now();
      trackGizmo("hair_prepare_start");
      const promise = (async () => {
        try {
          const res = await hairColorChangerFunction({ deviceId: id, image: dataUrl, prepare: true, instant: true });
          const data = res.data as PrepareResponse;
          if (generation !== photoGenerationRef.current) return null;
          if (data.sourceId) {
            setSourceId(data.sourceId);
            hairWrite("hair_source_id", data.sourceId);
            hairWrite("hair_source_id", data.sourceId, "session");
          }
          if (typeof data.freeRemaining === "number") {
            setFreeRemaining(data.freeRemaining);
            hairWrite("hair_free_used",String(data.freeRemaining));
            setFreeLimitHit(data.freeRemaining === 0);
          }
          trackGizmo("hair_prepare_ready", { elapsed_ms: Date.now() - startedAt });
          setPrepareResolved(true);
          return data.sourceId ?? null;
        } catch (error: any) {
          if (generation !== photoGenerationRef.current) return null;
          // PREPARE_LIMIT_REACHED and any other failure are non-fatal — the
          // "See it on me" flow falls back to sending the photo directly.
          const code = getErrorCode(error);
          console.error("Hair color prepare failed (non-fatal):", code || error);
          setPrepareResolved(true);
          return null;
        }
      })();
      preparePromiseRef.current = promise;
      return promise;
    },
    []
  );

  const handlePhotoDrop = async (files: File[]) => {
    if (files.length === 0 || openingPhoto || isGenerating || checkoutPreparing.current || checkoutOpen) return;
    setOpeningPhoto(true);
    setPhotoError("");
    let dataUrl: string;
    try {
      dataUrl = await resizeImage(files[0], 1536, 1536);
    } catch {
      setPhotoError("We couldn't open this photo. Choose a clear JPG, PNG or WebP photo under 10 MB.");
      setOpeningPhoto(false);
      return;
    }
    hairWrite("hairChangerImage", dataUrl, "session");
    photoGenerationRef.current++;
    preparePromiseRef.current = null;
    setSourceId(null);
    hairRemove("hair_source_id");
    hairRemove("hair_source_id", "session");
    setFreeResult(null); setFreeResults([]); setViewedResultIndex(null);
    setTeaserTiles(null); setTeaserFailed(false); teaserRequestedRef.current = false;
    setPhotoDataUrl(dataUrl);
    setOpeningPhoto(false);
    setPrepareResolved(false);
    trackGizmo("hair_upload", { tool: "hair", mode: "personal" });
    if (deviceId) {
      startPrepare(dataUrl, deviceId);
    }
  };

  const handlePhotoReject = (files: FileRejection[]) => {
    setPhotoError(files.some(file => file.errors.some(error => error.code === "file-too-large"))
      ? "This photo is too large. Choose an image under 10 MB."
      : "Choose a JPG, PNG or WebP photo under 10 MB.");
  };

  // If deviceId resolves after the photo was already dropped (race on first
  // load), kick off prepare once both are available.
  useEffect(() => {
    if (photoDataUrl && deviceId && !sourceId && !preparePromiseRef.current) {
      startPrepare(photoDataUrl, deviceId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoDataUrl, deviceId]);

  const filteredColors =
    selectedFamily === "all" ? HAIR_COLORS : HAIR_COLORS.filter((c) => c.family === selectedFamily);

  const openUpsellModal = (trigger: "auto" | "cta" | "limit") => {
    setUpsellTrigger(trigger);
    setUpsellModalOpen(true);
    trackGizmo("hair_upsell_modal_open", { trigger });
  };

  // Fire the teaser request in the background as soon as we have a sourceId
  // (i.e. right after the user's first successful free generation).
  useEffect(() => {
    if (studioMode || !photoDataUrl || !freeResult || !sourceId || !deviceId || teaserRequestedRef.current) return;
    teaserRequestedRef.current = true;
    (async () => {
      try {
        const res = await hairColorChangerFunction({ deviceId, sourceId, teaser: true });
        const data = res.data as TeaserResponse;
        if (data.teaserTiles?.length) {
          setTeaserTiles(data.teaserTiles);
        } else {
          setTeaserFailed(true);
        }
      } catch (error) {
        console.error("Error fetching hair color teaser:", error);
        setTeaserFailed(true);
      }
    })();
  }, [sourceId, deviceId, studioMode, photoDataUrl, freeResult]);

  const handleGenerate = async () => {
    if (!photoDataUrl || !selectedColorId || !deviceId || openingPhoto || isGenerating) return;
    const color = HAIR_COLORS.find((c) => c.id === selectedColorId);
    if (!color) return;

    const generationStarted = Date.now();
    setIsGenerating(true);
    setTipIdx(0);
    trackGizmo("hair_generate", { color_id: color.id, family: color.family, paid: false });

    // Await the prepare-ahead promise first (if one is in flight) so we reuse
    // its sourceId instead of re-uploading the photo. Show the "mapping"
    // stage only if that promise hasn't resolved yet — the usual case is it
    // already has, and the user goes straight to "painting".
    let resolvedSourceId = sourceId;
    if (!resolvedSourceId && preparePromiseRef.current) {
      setProgressStage("mapping");
      resolvedSourceId = await preparePromiseRef.current;
    }
    setProgressStage("painting");

    try {
      const res = await hairColorChangerFunction({
        deviceId,
        image: resolvedSourceId ? undefined : photoDataUrl,
        sourceId: resolvedSourceId || undefined,
        colorIds: [color.id],
      });
      const data = res.data as CallableResponse;

      if (data.sourceId) {
        setSourceId(data.sourceId);
        hairWrite("hair_source_id", data.sourceId);
        hairWrite("hair_source_id", data.sourceId, "session");
      }
      if (!data.results?.[0]?.imageUrl) throw new Error("PREVIEW_UNAVAILABLE");
      if (data.results?.[0]) {
        trackGizmo("hair_result_ready", { tool: "hair", mode: "personal", paid: false, color_id: color.id, elapsed_ms: Date.now() - generationStarted });
        setFreeResult(data.results[0]);
        setFreeResults((prev) => {
          const next = [
            ...prev,
            {
              colorId: color.id,
              colorName: color.name,
              hex: color.hex,
              image: data.results[0].imageUrl,
            },
          ];
          setViewedResultIndex(next.length - 1);
          return next;
        });
      }
      setFreeRemaining(data.freeRemaining);
      hairWrite("hair_free_used", String(data.freeRemaining));

      if (data.freeRemaining <= 0) {
        trackGizmo("hair_free_limit_hit");
        setFreeLimitHit(true);
      }
    } catch (error: any) {
      const code = getErrorCode(error);
      trackGizmo("hair_result_error", { tool: "hair", mode: "personal", reason: code === "FREE_LIMIT_REACHED" ? "free_limit" : "generation_failed" });
      if (code === "FREE_LIMIT_REACHED") {
        setFreeRemaining(0); hairWrite("hair_free_used","0");
        trackGizmo("hair_free_limit_hit");
        setFreeLimitHit(true);
        openUpsellModal("limit");
      } else {
        const { title, message } = mapErrorMessage(code);
        notifications.show({
          color: "red",
          title,
          message,
          autoClose: 8000,
        });
        if (code === "INVALID_IMAGE") {
          setPhotoDataUrl(null);
          hairRemove("hairChangerImage", "session");
        }
        if (code === "PAYMENT_REQUIRED" || code === "PAYMENT_INVALID") {
          setStudioMode(false);
        }
      }
    } finally {
      setIsGenerating(false);
      setProgressStage("idle");
    }
  };

  const pendingCheckoutSessionRef = useRef<string | null>(null);

  const fetchClientSecret = useCallback(async () => {
    trackGizmo("hair_checkout_start");
    const res = await fetch("/api/stripe/createHairColorSession", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ gizmoSession: getGizmoSession(), locale, deviceId, sourceId, resumeSessionId:hairRead("hair_pending_checkout") }),
    });
    const data = await res.json();
    if (!res.ok || typeof data.clientSecret !== "string") {
      setCheckoutOpen(false);
      if (data.sessionId) {hairWrite("hair_pending_checkout",data.sessionId);await verifyPaidSession(data.sessionId);}
      else setPaymentMessage("Checkout could not open. Please try again; no payment was taken.");
      throw new Error("Checkout unavailable");
    }
    pendingCheckoutSessionRef.current = data.sessionId ?? null;
    hairWrite("hair_pending_checkout",data.sessionId);
    setPendingPayment(data.sessionId);
    return data.clientSecret;
  }, [locale, deviceId, sourceId, verifyPaidSession]);

  // The checkout session uses redirect_on_completion: "never" — payment success
  // arrives here, not via a return-URL redirect.
  const handleCheckoutComplete = useCallback(() => {
    const sessionId = pendingCheckoutSessionRef.current;
    if (sessionId) verifyPaidSession(sessionId);
  }, [verifyPaidSession]);

  const handleOpenCheckout = async () => {
    if (!deviceId || !sourceId) {setPaymentMessage("Please wait for your photo to finish preparing before opening checkout.");return;}
    if (checkoutPreparing.current) return;
    checkoutPreparing.current = true;
    setPaymentMessage("Opening secure checkout…");
    trackCtaClick("hair_changer_paywall_cta");
    try {
      const secret = await fetchClientSecret();
      setCheckoutClientSecret(secret); setCheckoutOpen(true);setPaymentMessage("");
    } catch (error) {if (!(error instanceof Error) || error.message !== "Checkout unavailable") setPaymentMessage("Checkout could not connect. Please try again; no payment was taken.");}
    finally {checkoutPreparing.current = false;}
  };

  // Existing upsell card / paywall CTA — opens the teaser modal instead of checkout directly
  const handleUpsellCardClick = () => {
    openUpsellModal("cta");
  };

  const handleUpsellModalCta = () => {
    trackGizmo("hair_upsell_modal_cta", { trigger: upsellTrigger });
    setUpsellModalOpen(false);
    handleOpenCheckout();
  };

  useEffect(()=>{
    if(paymentMessage) requestAnimationFrame(()=>document.getElementById("hair-payment-status")?.scrollIntoView({block:"nearest"}));
  },[paymentMessage]);

  const handleDownload = async (imageUrl: string, colorId: string) => {
    setIsDownloading(true);
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("DOWNLOAD_UNAVAILABLE");
      savePreviewBlob(await response.blob(), colorId);
      trackGizmo("hair_result_download", { tool: "hair", mode: "personal", color_id: colorId });
    } catch {
      notifications.show({ color: "red", title: "Couldn't download", message: "Your preview is still here. Please try again." });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async (imageUrl: string, colorId: string) => {
    setIsSharing(true);
    const shareUrl = "https://www.coloranalysistech.com/ai-hair-color-try-on";
    try {
      const response = await fetch(imageUrl);
      if (!response.ok) throw new Error("DOWNLOAD_UNAVAILABLE");
      const blob = await response.blob();
      const file = new File([blob], "my-new-hair.jpg", { type: "image/jpeg" });
      if (typeof navigator !== "undefined" && navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My AI hair color try-on",
          text: "I tried on a new hair color with AI — see yours free:",
          url: shareUrl,
        });
        trackGizmo("hair_share", { method: "native", color_id: colorId });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        notifications.show({ title: "Link copied — image downloading", message: "Share the link, and enjoy your download.", color: "pink" });
        savePreviewBlob(blob, colorId);
        trackGizmo("hair_share", { method: "fallback", color_id: colorId });
      }
    } catch (error: any) {
      if (error?.name !== "AbortError") {
        console.error("Error sharing hair color result:", error);
        notifications.show({
          color: "red",
          title: "Couldn't share",
          message: "Something went wrong sharing your result. Please try downloading instead.",
        });
      }
    } finally {
      setIsSharing(false);
    }
  };

  const renderDimensional = useCallback(async (colorId: string) => {
    if (!deviceId || !sourceId || !paidSessionId || !HAIR_COLORS.some(color => color.id === colorId && color.family === "balayage")) throw new Error("Studio unavailable");
    trackGizmo("hair_generate", {color_id:colorId, family:"balayage", paid:true});
    const response = await hairColorChangerFunction({deviceId, sourceId, stripeSessionId:paidSessionId, colorIds:[colorId]});
    const data = response.data as CallableResponse;
    const result = data.results?.find(item => item.colorId === colorId);
    if (!result) throw new Error("Preview unavailable");
    return result.imageUrl;
  }, [deviceId, sourceId, paidSessionId]);

  const canGenerate = !!photoDataUrl && !!selectedColorId && !isGenerating && !openingPhoto;
  const selectedColor = selectedColorId ? HAIR_COLORS.find((c) => c.id === selectedColorId) : null;
  const viewedResult = viewedResultIndex !== null ? freeResults[viewedResultIndex] : null;

  const handleFamilySectionClick = (family: string, colorId: string) => {
    trackGizmo("hair_family_section_click", { family });
    setSelectedFamily(family);
    setSelectedColorId(colorId);
    document.getElementById("hair-tool")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Fallback swatch set for the upsell modal when teaser photos aren't ready/failed —
  // 9 evenly-spaced shades across the catalog for visual variety.
  const fallbackSwatches = Array.from({ length: 9 }, (_, i) =>
    HAIR_COLORS[Math.floor((i * HAIR_COLORS.length) / 9)]
  );
  const teaserItems = teaserTiles && teaserTiles.length > 0 && !teaserFailed
    ? teaserTiles.map((t) => ({ colorId: t.colorId, imageUrl: t.imageUrl as string | null, color: HAIR_COLORS.find((c) => c.id === t.colorId) }))
    : fallbackSwatches.map((c) => ({ colorId: c.id, imageUrl: null as string | null, color: c }));

  const demo = <HairColorDemo source={embedded ? "embedded" : "hair-tool"} onTryOnPhoto={(shade) => {
    trackCtaClick("hair_demo_to_upload");
    const color = HAIR_COLORS.find(c => c.id === shade.id);
    if (color) { setSelectedColorId(color.id); setSelectedFamily(color.family); }
    openUploadRef.current?.();
  }} />;

  return (
    <Container size="xl" px={embedded ? 0 : undefined} pt={embedded ? 0 : { base: "1rem", md: "2.5rem" }} pb={embedded ? 0 : { base: "3rem", md: "5rem" }} lang="en">
      <Suspense fallback={null}><QueryState onChange={setSearchParams} /></Suspense>
      {/* ============ Hero ============ */}
      {!embedded && <Box className={classes.hero}>
        <p className={classes.heroEyebrow}>{studioMode ? "Hair Color Studio" : "Free AI tool"}</p>
        <h1 className={classes.heroTitle}>AI Hair Color Changer</h1>
        <p className={classes.heroSubtitle}>
          Explore the example, then see a new hair color on your own photo.
        </p>
        <p className={classes.heroTrust}>
          {studioMode ? `${HAIR_COLORS.length} shades · favorites · downloads` : `${HAIR_COLORS.length} shades · no sign-up · 3 free try-ons`}
        </p>
        {!studioMode && !photoDataUrl && <Button className={classes.mobileUpload} size="lg" radius="xl" color="#b3265c" loading={openingPhoto} onClick={() => openUploadRef.current?.()}>Upload my photo</Button>}
      </Box>}

      {paymentMessage && <Box id="hair-payment-status" role="status" mb="lg" p="md" style={{background:"white",border:"1px solid #d7dfd9",borderRadius:12}}>
        <Text mb="sm">{paymentMessage}</Text>
        <Group>{pendingPayment && <Button disabled={verifyingPayment} onClick={()=>verifyPaidSession(pendingPayment)}>{verifyingPayment ? "Checking payment…" : "Check my payment again"}</Button>}
        <Button component="a" href={locale === "en" ? "/contact" : `/${locale}/contact`} variant="subtle">Contact support</Button></Group>
      </Box>}

      {/* ============ Tool ============ */}
      {photoError && !photoDataUrl && <Text role="alert" c="red" size="sm" mb="md">{photoError}</Text>}
      {requestedShade && selectedColor && !photoDataUrl && <Box mb="lg" p="md" style={{border:"1px solid #d7dfd9",borderRadius:12,background:"white"}} aria-label="Preselected hair color">
        <Group gap="sm"><span style={{width:28,height:28,borderRadius:"50%",background:selectedColor.hex,border:"1px solid #0002"}} aria-hidden="true" /><Text fw={600}>Selected from your shortlist: {selectedColor.name}</Text></Group>
        <Text size="sm" mt="xs">Upload your photo below to preview this shade. You can change the color before generating.</Text>
      </Box>}
      {!studioMode && recommendedColors.length > 0 && <Box mb="lg" p="md" style={{border:"1px solid #d7dfd9",borderRadius:12,background:"white"}} aria-label="Your recommended hair shades">
        <Text fw={600} mb="xs">Your finder shortlist</Text>
        <Text size="sm" c="dimmed" mb="sm">Start with these shades, then explore the full collection. Selecting a shade does not use a try-on.</Text>
        <Group gap="xs">{recommendedColors.map(color => <Button key={color.id} variant={selectedColorId === color.id ? "filled" : "outline"} color="teal" aria-pressed={selectedColorId === color.id} onClick={() => {
          setSelectedColorId(color.id);
          setSelectedFamily(color.family);
          trackGizmo("hair_shortlist_select", {color_id:color.id});
        }} leftSection={<span style={{width:18,height:18,borderRadius:"50%",background:color.hex,border:"1px solid #0003"}} aria-hidden="true" />}>{color.name}</Button>)}</Group>
      </Box>}
      {!studioMode && (
        <Box id="hair-tool" className={classes.toolCard}>
          {!photoDataUrl && (
            <Box className={classes.emptyToolGrid}>
            {demo}
            <Box className={classes.uploadStage} ref={uploadSectionRef}>
              <h2 className={classes.sectionTitle}>Your turn. Start with a selfie.</h2>
              <Text size="sm" c="dimmed" mb="md">Choose from {HAIR_COLORS.length} shades. Your first 3 previews are free. No account needed.</Text>
              <Dropzone
                onDrop={handlePhotoDrop}
                onReject={handlePhotoReject}
                loading={openingPhoto}
                disabled={openingPhoto}
                openRef={openUploadRef}
                useFsAccessApi={false}
                maxSize={10 * 1024 ** 2}
                accept={["image/jpeg", "image/png", "image/webp"]}
                multiple={false}
                radius="lg"
                className={classes.dropzone}
              >
                <Flex direction="column" align="center" justify="center" gap={6} style={{ pointerEvents: "none" }}>
                  <Dropzone.Accept>
                    <IconDownload style={{ width: rem(40), height: rem(40), color: "#22c55e" }} stroke={1.5} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconX style={{ width: rem(40), height: rem(40), color: "#ef4444" }} stroke={1.5} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconCloudUpload style={{ width: rem(40), height: rem(40), color: "#8b8b98" }} stroke={1.5} />
                  </Dropzone.Idle>
                  <span className={classes.uploadButton}>Choose my photo</span>
                  <Text size="sm" ta="center" style={{ color: "#8b8b98" }}>
                    Clear, front-facing photos work best
                  </Text>
                  <Text size="xs" c="dimmed">JPG · PNG · WebP · 10 MB</Text>
                </Flex>
              </Dropzone>
              <p className={classes.privacyMicrocopy}>
                Your photo is processed to create previews. It is not added to a public gallery.
              </p>
              <Flex className={classes.threeStepStrip} justify="center" align="center" gap="xs" wrap="wrap">
                <span className={classes.threeStepItem}>1. Upload</span>
                <span className={classes.threeStepArrow}>&rarr;</span>
                <span className={classes.threeStepItem}>2. Pick a shade</span>
                <span className={classes.threeStepArrow}>&rarr;</span>
                <span className={classes.threeStepItem}>3. See it on you</span>
              </Flex>
              <Text size="sm" c="dimmed" mt="md">Use a well-lit photo with your hair visible. After upload, pick a shade, compare with your original and save the looks you want to show your stylist.</Text>
            </Box>
            </Box>
          )}

          {photoDataUrl && (
            <Box className={classes.toolGrid}>
              {/* ---------- Photo column ---------- */}
              <Box className={classes.photoColumn}>
                <Box className={classes.photoFrame}>
                  {viewedResult ? (
                    <CompareSlider
                      beforeSrc={photoDataUrl}
                      afterSrc={viewedResult.image}
                      beforeAlt="Your original photo"
                      afterAlt="Your hair color result"
                      className={classes.compareSliderFill}
                    />
                  ) : (
                    <img src={photoDataUrl} alt="Your uploaded photo" className={classes.photoImg} />
                  )}

                  {isGenerating && (
                    <Box className={classes.progressOverlay}>
                      <Box className={classes.progressModule}>
                        {progressStage === "mapping" ? (
                          <IconWand size={22} className={classes.progressIcon} />
                        ) : (
                          <IconSparkles size={22} className={classes.progressIcon} />
                        )}
                        <Text fw={600} size="sm" className={classes.progressStageLabel}>
                          {progressStage === "mapping"
                            ? "Mapping your hair"
                            : `Painting ${selectedColor?.name || "your color"}`}
                        </Text>
                        <Text size="xs" className={classes.progressCopy}>
                          {progressStage === "mapping"
                            ? "First try-on takes about a minute."
                            : selectedColor?.family === "balayage"
                              ? "Dimensional shades take about a minute."
                              : "Just a few seconds for flat shades."}
                        </Text>
                        <Box className={classes.progressBarTrack}>
                          <Box
                            className={`${classes.progressBarFill} ${
                              progressStage === "mapping" ? classes.progressBarFillSlow : classes.progressBarFillFast
                            }`}
                          />
                        </Box>
                        <Text size="xs" className={classes.progressTip}>
                          {STYLING_TIPS[tipIdx]}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Dropzone
                  onDrop={handlePhotoDrop}
                  onReject={handlePhotoReject}
                  loading={openingPhoto}
                  useFsAccessApi={false}
                  maxSize={10 * 1024 ** 2}
                  accept={["image/jpeg", "image/png", "image/webp"]}
                  multiple={false}
                  disabled={openingPhoto || isGenerating || checkoutOpen || verifyingPayment}
                  role="button"
                  aria-label="Change hair photo"
                  p="xs"
                  mt="sm"
                  radius="md"
                >
                  <Text size="sm" ta="center" style={{ pointerEvents: "none" }}>Change photo</Text>
                </Dropzone>
                {photoError && <Text role="alert" c="red" size="sm" mt="sm">{photoError}</Text>}
                <Text size="xs" c="dimmed" mt={6}>Save previews you want to keep before changing photos. Your remaining free previews carry over.</Text>

                {viewedResult && onUsePreview && <Button fullWidth mt="md" color="teal" onClick={()=>onUsePreview(viewedResult.image,viewedResult.colorName)}>Use this hair color in my look</Button>}
                <Flex justify="space-between" align="center" mt="md" wrap="wrap" gap="sm">
                  <Badge color="pink" variant="light" size="lg">
                    {Math.max(freeRemaining, 0)} of {FREE_GENERATION_LIMIT} free try-ons left
                  </Badge>
                  {viewedResult && !isGenerating && (
                    <Flex gap="sm">
                      <Button
                        onClick={() => handleDownload(viewedResult.image, viewedResult.colorId)}
                        loading={isDownloading}
                        variant="outline"
                        radius="xl"
                        size="sm"
                        leftSection={<IconDownload size={16} />}
                      >
                        Download
                      </Button>
                      <Button
                        variant="outline"
                        radius="xl"
                        size="sm"
                        loading={isSharing}
                        leftSection={<IconShare size={16} />}
                        onClick={() => handleShare(viewedResult.image, viewedResult.colorId)}
                      >
                        Share
                      </Button>
                    </Flex>
                  )}
                </Flex>

                {freeResults.length >= 2 && (
                  <>
                    <Flex className={classes.historyStrip} wrap="wrap">
                      {freeResults.map((result, idx) => (
                        <button
                          key={`${result.colorId}-${idx}`}
                          className={`${classes.historyThumb} ${idx === viewedResultIndex ? classes.historyThumbActive : ""}`}
                          onClick={() => setViewedResultIndex(idx)}
                        >
                          <img src={result.image} alt={result.colorName} className={classes.historyThumbImg} />
                          <span className={classes.historyThumbLabel}>{result.colorName}</span>
                        </button>
                      ))}
                    </Flex>
                    <p className={classes.historyMicrocopy}>
                      Comparing {freeResults.length} shades side by side? The studio does this with all {HAIR_COLORS.length}.
                    </p>
                  </>
                )}
              </Box>

              {/* ---------- Shade picker column ---------- */}
              <Box className={classes.pickerColumn}>
                <h2 className={classes.sectionTitle}>Pick a hair color</h2>

                <Flex gap="xs" wrap="wrap" mb="md" className={classes.familyPillRow}>
                  <button
                    className={`${classes.familyPill} ${selectedFamily === "all" ? classes.familyPillActive : ""}`}
                    onClick={() => setSelectedFamily("all")}
                  >
                    All
                  </button>
                  {HAIR_COLOR_FAMILIES.map((fam) => (
                    <button
                      key={fam.key}
                      className={`${classes.familyPill} ${selectedFamily === fam.key ? classes.familyPillActive : ""}`}
                      onClick={() => setSelectedFamily(fam.key)}
                    >
                      {fam.label}
                    </button>
                  ))}
                </Flex>

                <Box className={classes.swatchPanel}>
                  <SimpleGrid cols={{ base: 4, sm: 5 }} spacing="md">
                    {filteredColors.map((color) => (
                      <button
                        key={color.id}
                        className={`${classes.swatch} ${selectedColorId === color.id ? classes.swatchSelected : ""}`}
                        onClick={() => setSelectedColorId(color.id)}
                      >
                        <span className={classes.swatchCircle} style={{ background: color.hex }} />
                        <span className={classes.swatchName}>{color.name}</span>
                      </button>
                    ))}
                  </SimpleGrid>
                </Box>

                {!freeLimitHit && (
                  <Box className={classes.pickerFooter}>
                    {selectedColor ? (
                      <Flex align="center" gap="sm" className={classes.selectedShadeCard}>
                        <span className={classes.selectedShadeCircle} style={{ background: selectedColor.hex }} />
                        <Box style={{ flex: 1 }}>
                          <Text fw={600} size="sm" style={{ color: "#16181d" }}>
                            {selectedColor.name}
                          </Text>
                          <Text size="xs" c="dimmed" tt="capitalize">
                            {selectedColor.family}
                          </Text>
                        </Box>
                        {selectedColor.family === "balayage" && (
                          <Badge size="xs" variant="light" color="gray">
                            salon technique · ~1 min
                          </Badge>
                        )}
                      </Flex>
                    ) : (
                      <Text size="sm" c="dimmed" ta="center" className={classes.selectedShadeCard}>
                        Choose a shade above to preview it on your photo.
                      </Text>
                    )}
                    <Button
                      size="lg"
                      radius="xl"
                      fullWidth
                      mt="sm"
                      disabled={!canGenerate}
                      loading={isGenerating}
                      style={{ background: "#16181d", color: "white" }}
                      onClick={handleGenerate}
                    >
                      See it on me
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Upsell — shown after every free result, or as the paywall when limit hit */}
          {(freeResult || freeLimitHit || (onStudioAccess && photoDataUrl)) && (
            <Box className={classes.upsellCard}>
              <h3 className={classes.upsellTitle}>
                {freeLimitHit
                  ? "You've used your 3 free try-ons"
                  : "Want the full salon experience?"}
              </h3>
              <p className={classes.upsellSub}>
                {freeLimitHit
                  ? `Unlock the full ${HAIR_COLORS.length}-shade studio to keep going.`
                  : `Switch between solid shades in your browser, compare favorites, and render balayage when you choose it.`}
              </p>
              <Text size="sm" className={classes.upsellValueLine}>One photo · saved favorites · downloadable comparisons</Text>
              <Button
                size="lg"
                radius="xl"
                style={{ background: "#b3265c", color: "white" }}
                onClick={handleUpsellCardClick}
              >
                Unlock all {HAIR_COLORS.length} shades — ${PRICING.HAIR_COLOR_CHANGER.BASE_PRICE}
              </Button>
            </Box>
          )}
        </Box>
      )}


      {/* Paid assets are verified server-side; a saved session alone cannot unlock them. */}
      {studioMode && onStudioAccess && <p role="status">Opening your combined studio…</p>}
      {studioMode && !onStudioAccess && deviceId && paidSessionId && <HairStudio
        key={sourceId || "missing-photo"}
        deviceId={deviceId} sourceId={sourceId} stripeSessionId={paidSessionId}
        initialShade={selectedColorId} shortlist={recommendedColors.map(color => color.id)}
        originalPhoto={photoDataUrl} renderDimensional={renderDimensional} onUsePreview={onUsePreview}
      />}

      {/* Mobile sticky action bar — mirrors the desktop pinned footer so
          "See it on me" never requires scrolling on small screens. */}
      {!studioMode && photoDataUrl && !freeLimitHit && (
        <Box className={classes.stickyActionBar}>
          <Flex align="center" gap="sm">
            {selectedColor ? (
              <>
                <span className={classes.stickyShadeCircle} style={{ background: selectedColor.hex }} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="sm" truncate style={{ color: "#16181d" }}>
                    {selectedColor.name}
                  </Text>
                  <Text size="xs" c="dimmed">
                    {Math.max(freeRemaining, 0)} tries left
                  </Text>
                </Box>
              </>
            ) : (
              <Text size="sm" c="dimmed" style={{ flex: 1 }}>
                Pick a shade above
              </Text>
            )}
            <Button
              radius="xl"
              disabled={!canGenerate}
              loading={isGenerating}
              style={{ background: "#16181d", color: "white" }}
              onClick={handleGenerate}
            >
              See it on me
            </Button>
          </Flex>
        </Box>
      )}

      {/* ============ How it works ============ */}
      {!embedded && <>
      <Box className={classes.folio}>
        <h2 className={classes.folioTitle}>How it works</h2>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <Box className={classes.stepCard}>
            <p className={classes.stepNumber}>1</p>
            <Text fw={600} mb={4}>Upload a selfie</Text>
            <Text size="sm" c="dimmed">A clear, front-facing photo works best.</Text>
          </Box>
          <Box className={classes.stepCard}>
            <p className={classes.stepNumber}>2</p>
            <Text fw={600} mb={4}>Pick a hair color</Text>
            <Text size="sm" c="dimmed">Choose from {HAIR_COLORS.length} shades. Three previews are free; further studio access is optional.</Text>
          </Box>
          <Box className={classes.stepCard}>
            <p className={classes.stepNumber}>3</p>
            <Text fw={600} mb={4}>Compare your result</Text>
            <Text size="sm" c="dimmed">Compare color directions on your photo before deciding what to discuss with your colorist.</Text>
          </Box>
        </SimpleGrid>
      </Box>

      {/* ============ Per-family SEO sections ============ */}
      <Box className={classes.folio} ref={familySectionRef}>
        <h2 className={classes.folioTitle}>Browse by shade family</h2>
        {HAIR_COLOR_FAMILIES.map((fam) => {
          const familyColors = HAIR_COLORS.filter((c) => c.family === fam.key);
          return (
            <Box key={fam.key} className={classes.familySection}>
              <h3 className={classes.familySectionTitle}>Try On {fam.label} Hair Colors</h3>
              <p className={classes.familySectionCopy}>{FAMILY_COPY[fam.key]}</p>
              <Flex gap="sm" wrap="wrap" className={classes.familySectionChips}>
                {familyColors.map((color) => (
                  <button
                    key={color.id}
                    className={classes.familyChip}
                    onClick={() => handleFamilySectionClick(fam.key, color.id)}
                  >
                    <span className={classes.familyChipCircle} style={{ background: color.hex }} />
                    <span>{color.name}</span>
                  </button>
                ))}
              </Flex>
            </Box>
          );
        })}
      </Box>

      {/* ============ What to expect ============ */}
      <Box className={classes.folio}>
        <h2 className={classes.folioTitle}>What to expect</h2>
        <Box className={classes.expectCard}>
          <Text size="sm" c="dimmed" mb={10}>
            This tool works best with clear, front-facing photos and even lighting — it uses your actual features, so a
            blurry or heavily shadowed photo will produce a less accurate result.
          </Text>
          <Text size="sm" c="dimmed" mb={10}>
            Dimensional shades like balayage take a little longer to render than flat colors, since they're a
            hand-painted, multi-tone technique rather than a single color swap.
          </Text>
          <Text size="sm" c="dimmed">
            Results are a realistic preview, not a salon guarantee — think of it as a way to narrow down your options
            before you sit in the chair, not an exact color-match promise.
          </Text>
        </Box>
      </Box>

      {/* ============ FAQ ============ */}
      <Box className={classes.folio}>
        <h2 className={classes.folioTitle}>Frequently asked questions</h2>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {HAIR_FAQS.map((faq) => (
            <Box key={faq.question} className={classes.faqCard}>
              <h3 className={classes.faqQuestion}>{faq.question}</h3>
              <p className={classes.faqAnswer}>{faq.answer}</p>
            </Box>
          ))}
        </SimpleGrid>
      </Box>

      {/* ============ Cross-links ============ */}
      <Box className={classes.folio}>
        <p className={classes.crossLinkCopy}>
          Curious about your best colors, not just hair? <a href={`/${locale === "en" ? "" : locale + "/"}virtual-color-draping`.replace("//", "/")}>Drape full color seasons</a> the same way, or get a complete{" "}
          <a href={`/${locale === "en" ? "" : locale + "/"}ai-color-analysis`.replace("//", "/")}>AI color analysis</a> of your skin, hair, and eye undertones.
        </p>
      </Box>

      <Box mt={64}>
        <EmailResultsCapture source="ai-hair-color-changer" />
      </Box>
      </>}

      {/* Upsell Modal — teaser mosaic */}
      <Modal
        opened={upsellModalOpen}
        onClose={() => setUpsellModalOpen(false)}
        size="lg"
        centered
        radius="lg"
        padding="xl"
        withCloseButton
        closeButtonProps={{'aria-label':'Close hair upgrade'}}
      >
        <h2 className={classes.upsellModalHeadline}>
          This is actually you — in all {HAIR_COLORS.length} shades
        </h2>

        <SimpleGrid cols={3} spacing="sm">
          {teaserItems.map((item) => (
            <Box key={item.colorId}>
              <Box className={classes.teaserTile}>
                {item.imageUrl ? (
                  <img src={item.imageUrl} alt={item.color?.name || "Hair color preview"} className={classes.teaserTileImg} />
                ) : (
                  <span className={classes.teaserTileSwatch} style={{ background: item.color?.hex || "#e2e8f0" }} />
                )}
                <span className={classes.teaserLockBadge}>
                  <IconLock size={12} />
                </span>
              </Box>
              <p className={classes.teaserTileLabel}>{item.color?.name || "Locked shade"}</p>
            </Box>
          ))}
        </SimpleGrid>

        <Box className={classes.upsellBenefitGrid}>
          {[
            `All ${HAIR_COLORS.length} shades on your actual photo`,
            "Balayage, vivids & pastels included",
            "Download every image",
            "One-time payment — no subscription",
          ].map((benefit) => (
            <Flex key={benefit} className={classes.upsellBenefitItem}>
              <IconCheck size={16} className={classes.upsellBenefitIcon} />
              <span>{benefit}</span>
            </Flex>
          ))}
        </Box>

        <p className={classes.upsellModalCopy}>
          Your 9 of {HAIR_COLORS.length}. Unlock the full collection: switch solid colors locally, compare favorites, and render dimensional looks separately.
        </p>

        <Button
          size="lg"
          radius="xl"
          fullWidth
          style={{ background: "#b3265c", color: "white" }}
          onClick={handleUpsellModalCta}
        >
          Unlock all {HAIR_COLORS.length} shades — ${PRICING.HAIR_COLOR_CHANGER.BASE_PRICE}
        </Button>

        <p className={classes.upsellModalReassurance}>
          One photo · one-time payment · all {HAIR_COLORS.length} shades
        </p>
      </Modal>

      {/* Checkout Modal */}
      <Modal opened={checkoutOpen} onClose={() => setCheckoutOpen(false)} size="lg" centered title="Unlock the Hair Color Studio" closeButtonProps={{'aria-label':'Close hair checkout'}}>
        {checkoutClientSecret && <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret:checkoutClientSecret, onComplete: handleCheckoutComplete }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>}
      </Modal>

    </Container>
  );
}

export default function Content(props: ContentProps) {
  return <ContentInner {...props} />;
}
